<div class="mg-card">

  <!-- Header -->
  <div class="mg-header">
    <div class="mg-header-left">
      <span class="mg-eyebrow">Analytics</span>
      <h3 class="mg-title">Monthly Signup Trends</h3>
      <p class="mg-subtitle" *ngIf="!isLoading && !errorMessage">
        {{ data.length }} month{{ data.length !== 1 ? 's' : '' }} of data &nbsp;·&nbsp;
        {{ totalAll }} total signups &nbsp;·&nbsp;
        GH₵ {{ totalRevenue | number:'1.2-2' }} collected
      </p>
    </div>
    <button class="mg-refresh-btn" (click)="loadData()" [disabled]="isLoading">
      <span [class.spinning]="isLoading">↻</span>
    </button>
  </div>

  <!-- Metric Toggles -->
  <div class="mg-metrics" *ngIf="!errorMessage">
    <button
      *ngFor="let m of metrics"
      class="mg-metric-btn"
      [class.active]="activeMetric === m.key"
      [style.--accent]="m.color"
      (click)="setMetric(m.key)">
      <span class="mg-metric-dot" [style.background]="activeMetric === m.key ? 'rgba(255,255,255,0.8)' : m.color"></span>
      {{ m.label }}
    </button>
  </div>

  <!-- Loading -->
  <div *ngIf="isLoading" class="mg-chart-area">
    <div class="mg-skeleton-chart">
      <div class="mg-skel-bar" *ngFor="let b of skeletonBars" [style.height]="(b * 17) + '%'"></div>
    </div>
  </div>

  <!-- Error -->
  <div *ngIf="!isLoading && errorMessage" class="mg-state-box">
    <div class="mg-state-icon">⚠️</div>
    <div class="mg-state-title">Could not load chart</div>
    <div class="mg-state-sub">{{ errorMessage }}</div>
    <button class="mg-refresh-btn solid" (click)="loadData()" style="margin-top:12px">Retry</button>
  </div>

  <!-- Empty -->
  <div *ngIf="!isLoading && !errorMessage && data.length === 0" class="mg-state-box">
    <div class="mg-state-icon">📊</div>
    <div class="mg-state-title">No data yet</div>
    <div class="mg-state-sub">Signup data will appear here once available</div>
  </div>

  <!-- Line Chart -->
  <div *ngIf="!isLoading && !errorMessage && data.length > 0"
       class="mg-chart-wrap"
       (mouseleave)="onChartLeave()">

    <svg
      [attr.viewBox]="'0 0 ' + svgWidth + ' ' + svgHeight"
      class="mg-svg"
      xmlns="http://www.w3.org/2000/svg">

      <defs>
        <!-- Gradient fill under line -->
        <linearGradient [attr.id]="'areaGrad_' + activeMetric" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   [attr.stop-color]="activeMetricMeta.color" stop-opacity="0.18"/>
          <stop offset="100%" [attr.stop-color]="activeMetricMeta.color" stop-opacity="0.01"/>
        </linearGradient>
        <!-- Glow filter for the line -->
        <filter id="lineGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2.5" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      <!-- Y-axis grid lines & labels -->
      <g *ngFor="let tick of yTicks">
        <line
          [attr.x1]="padL" [attr.x2]="svgWidth - padR"
          [attr.y1]="yTickY(tick)" [attr.y2]="yTickY(tick)"
          class="mg-grid-line"/>
        <text
          [attr.x]="padL - 8"
          [attr.y]="yTickY(tick) + 4"
          class="mg-axis-label"
          text-anchor="end">{{ tick }}</text>
      </g>

      <!-- Axes -->
      <line [attr.x1]="padL" [attr.x2]="padL"
            [attr.y1]="padT" [attr.y2]="padT + chartH()"
            class="mg-axis-line"/>
      <line [attr.x1]="padL" [attr.x2]="svgWidth - padR"
            [attr.y1]="padT + chartH()" [attr.y2]="padT + chartH()"
            class="mg-axis-line"/>

      <!-- Year separator lines -->
      <g *ngFor="let d of data; let i = index">
        <g *ngIf="i > 0 && d.year !== data[i-1].year">
          <line
            [attr.x1]="pointX(i)" [attr.x2]="pointX(i)"
            [attr.y1]="padT" [attr.y2]="padT + chartH()"
            class="mg-year-line" stroke-dasharray="4 3"/>
          <text
            [attr.x]="pointX(i) + 4"
            [attr.y]="padT + 12"
            class="mg-year-label">{{ d.year }}</text>
        </g>
      </g>

      <!-- Area fill -->
      <path
        [attr.d]="areaPath"
        [attr.fill]="'url(#areaGrad_' + activeMetric + ')'"
        class="mg-area"/>

      <!-- Main line -->
      <path
        [attr.d]="linePath"
        [attr.stroke]="activeMetricMeta.color"
        class="mg-line"
        filter="url(#lineGlow)"/>

      <!-- X-axis labels -->
      <text
        *ngFor="let d of data; let i = index"
        [attr.x]="pointX(i)"
        [attr.y]="padT + chartH() + 18"
        class="mg-x-label"
        text-anchor="middle">{{ formatMonthLabel(d) }}</text>

      <!-- Vertical hover line -->
      <line
        *ngIf="hoveredIndex !== null"
        [attr.x1]="pointX(hoveredIndex)" [attr.x2]="pointX(hoveredIndex)"
        [attr.y1]="padT" [attr.y2]="padT + chartH()"
        class="mg-hover-line"/>

      <!-- Invisible hit targets for each point -->
      <rect
        *ngFor="let d of data; let i = index"
        [attr.x]="pointX(i) - (data.length > 1 ? chartW() / (data.length - 1) / 2 : chartW() / 2)"
        [attr.y]="padT"
        [attr.width]="data.length > 1 ? chartW() / (data.length - 1) : chartW()"
        [attr.height]="chartH()"
        fill="transparent"
        style="cursor:crosshair"
        (mouseenter)="onPointHover(i)"/>

      <!-- Data points -->
      <g *ngFor="let d of data; let i = index">
        <!-- Outer glow ring (only on hover) -->
        <circle
          *ngIf="hoveredIndex === i"
          [attr.cx]="pointX(i)"
          [attr.cy]="pointY(i)"
          r="10"
          [attr.fill]="activeMetricMeta.color"
          opacity="0.15"/>
        <!-- White ring -->
        <circle
          [attr.cx]="pointX(i)"
          [attr.cy]="pointY(i)"
          [attr.r]="hoveredIndex === i ? 6 : 4"
          fill="white"
          [attr.stroke]="activeMetricMeta.color"
          [attr.stroke-width]="hoveredIndex === i ? 2.5 : 1.5"
          class="mg-point"/>
        <!-- Value label (always show on top of hovered) -->
        <text
          *ngIf="hoveredIndex === i"
          [attr.x]="pointX(i)"
          [attr.y]="pointY(i) - 12"
          class="mg-point-label"
          [attr.fill]="activeMetricMeta.color"
          text-anchor="middle">{{ chartValues[i] }}</text>
      </g>

      <!-- Tooltip box -->
    <!-- Tooltip box -->
<g *ngIf="hoveredIndex !== null">
  <rect
    [attr.x]="tooltipX(hoveredIndex)"
    [attr.y]="tooltipY(hoveredIndex)"
    width="136" height="62"
    class="mg-tooltip-bg" rx="8"/>
  <text
    [attr.x]="tooltipX(hoveredIndex) + 68"
    [attr.y]="tooltipY(hoveredIndex) + 18"
    class="mg-tooltip-month"
    text-anchor="middle">
    {{ titleCase(data[hoveredIndex].monthName) }} {{ data[hoveredIndex].year }}
  </text>
  <text
    [attr.x]="tooltipX(hoveredIndex) + 68"
    [attr.y]="tooltipY(hoveredIndex) + 34"
    class="mg-tooltip-stat"
    text-anchor="middle">
    {{ activeMetricMeta.label }}: {{ chartValues[hoveredIndex] }}
  </text>
  <text
    [attr.x]="tooltipX(hoveredIndex) + 68"
    [attr.y]="tooltipY(hoveredIndex) + 50"
    class="mg-tooltip-stat"
    text-anchor="middle">
    GH&#8373; {{ data[hoveredIndex].totalAmountCollected | number:'1.2-2' }}
  </text>
</g>